import { CommonModule } from '@angular/common';
import { Component, inject, input, OnInit, signal } from '@angular/core';
import { AbstractControl, FormControl, NonNullableFormBuilder, ReactiveFormsModule, ValidationErrors, ValidatorFn, Validators } from '@angular/forms';
import { Router } from '@angular/router';
import { NavbarComponent } from "../../../template/components/navbar/navbar.component";
import { UserService } from '../../services/user.service';
import { finalize, Observable } from 'rxjs';
import { CreateUser } from '../../models/create-user.model';
import { ToastService } from '../../../shared/services/toast.service';
import { LoadingButtonComponent } from "../../../shared/components/loading-button/loading-button.component";
import { User } from '../../models/user.model';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { ConfirmModal } from '../../../shared/components/confirm-modal/confirm-modal.component';

type CreateUserForm = {
  [K in keyof CreateUser]: FormControl<CreateUser[K]>;
};

export function italianPhoneValidator(): ValidatorFn {
  return (control: AbstractControl): ValidationErrors | null => {
    const value = control.value?.toString().trim();

    // Fisso: inizia per 0, da 7 a 11 cifre totali
    const fixedLinePattern = /^0\d{6,10}$/;

    // Mobile: inizia per 3, da 9 a 10 cifre totali
    const mobilePattern = /^3\d{8,9}$/;

    const isValid = fixedLinePattern.test(value) || mobilePattern.test(value);

    return isValid ? null : { invalidPhone: true };
  };
}

@Component({
  selector: 'app-edit-user',
  imports: [CommonModule, ReactiveFormsModule, NavbarComponent, LoadingButtonComponent],
  templateUrl: './user-edit.component.html'
})
export class UserEditComponent implements OnInit {
  private readonly router = inject(Router);
  private readonly userSrv = inject(UserService);
  private readonly toastSrv = inject(ToastService);
  private readonly fb = inject(NonNullableFormBuilder);
  private readonly modalSrv = inject(NgbModal);

  readonly userDetail = input<User>();
  userDisabled = signal(false);

  loading = false;
  resendFirstAccessEmailLoading = false;

  navList: { label: string, routePath: string }[] = [];

  userForm = this.fb.group({
    name: ['', [Validators.required, Validators.maxLength(255)]],
    surname: ['', [Validators.required, Validators.maxLength(255)]],
    email: ['', [Validators.required, Validators.email]],
    telephone: ['', [Validators.required, italianPhoneValidator()]],
  });

  ngOnInit(): void {
    const userDetail = this.userDetail();

    if (userDetail) {
      this.navList = [{ label: 'Lista Utenti', routePath: '/users' }, { label: `${userDetail.name} ${userDetail.surname}` , routePath: `/users/${userDetail.uid}` }];

      this.userForm.patchValue({
        name: userDetail.name,
        surname: userDetail.surname,
        email: userDetail.email,
        telephone: userDetail.telephone
      });
      this.userForm.disable();
      this.userDisabled.set(userDetail.disabled);
    } else {
      this.navList = [{ label: 'Lista Utenti', routePath: '/users' }, { label: 'Nuovo Utente', routePath: '/users/new' }];
    }
  }

  onSubmit() {
    if (this.userForm.valid) {
      this.loading = true;
      console.log('Form Submitted', this.userForm.value);
      const newUser: CreateUser = this.userForm.getRawValue();
      this.userSrv.createAdminUser(newUser)
        .pipe(finalize(() => this.loading = false))
        .subscribe(
          {
            next: () => {
              this.toastSrv.show({ type: 'success', message: 'Utente creato' });
              this.router.navigate(['/users']);
            }
          });
    }
  }

  onCancel() {
    this.userForm.reset();
  }

  confirmEnableDisableUser(): void {
    const modalRef = this.modalSrv.open(ConfirmModal, { centered: true, windowClass: 'qai-modal' });
    modalRef.componentInstance.title = `${this.userDisabled() === true ? 'Abilita' : 'Disabilita'} Utente`;
    modalRef.componentInstance.body = `Stai per ${this.userDisabled() === true ? 'abilitare' : 'disabilitare'} l'utente ${this.userDetail()?.name} ${this.userDetail()?.surname}.`;
    modalRef.result.then(
      (result) => {
        this.toggleEnableDisableUser();
        console.debug(`Closed with: ${result}`);
      },
      (reason) => {
        console.debug(`Dismissed with: ${reason}`);
      },
    );
  }

  toggleEnableDisableUser() {
    const userDetail = this.userDetail();

    if (userDetail) {
      this.loading = true;

      let changeStatus: Observable<{}>;

      if (this.userDisabled() === true) {
        changeStatus = this.userSrv.enableUser(userDetail.uid);
      } else {
        changeStatus = this.userSrv.disableUser(userDetail.uid);
      }

      changeStatus
        .pipe(finalize(() => this.loading = false))
        .subscribe(() => {
          this.toastSrv.show({ type: 'success', message: `Utente ${this.userDisabled() ? 'Abilitato' : 'Disabilitato'}!` });
          this.userDisabled.set(this.userDisabled() === true ? false : true);
        });
    }
  }

  resendFirstAccessEmail() {
    const userDetail = this.userDetail();
    if (userDetail) {
      this.resendFirstAccessEmailLoading = true;
      this.userSrv.resendFirstAccessEmail(userDetail.email)
        .pipe(finalize(() => this.resendFirstAccessEmailLoading = false))
        .subscribe({
          next: () => {
            this.toastSrv.show({ type: 'success', message: `Utente ${userDetail.name} ${userDetail.surname} - Email di primo accesso re-inviata!` });
          }
        });
    }
  }

  get name() { return this.userForm.get('name'); }
  get surname() { return this.userForm.get('surname'); }
  get email() { return this.userForm.get('email'); }
  get telephone() { return this.userForm.get('telephone'); }
}
